// Revisado: 2025-12-18 I18n Updated
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ROUTES } from '../../routes';
import { API_BASE_URL } from '../../constants';
import { useAuth } from '../../context/AuthContext';
import LoadingScreen from '../shared/LoadingScreen';
import FadeImage from '../shared/FadeImage';
import { useTranslation } from 'react-i18next';
import SupportEvents from './SupportEvents';

const SupportDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isAdmin } = useAuth();
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [events, setEvents] = useState([]);
    const [eventSkills, setEventSkills] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchSupportDetails = async () => {
            try {
                setLoading(true);

                // Construct headers with token if available
                const response = await fetch(`${API_BASE_URL}/api/supports/${id}`);

                if (!response.ok) throw new Error('Failed to fetch support details');
                const result = await response.json();
                setData(result);

                // Fetch Events
                const eventsResponse = await fetch(`${API_BASE_URL}/api/supports/${id}/events`);
                if (eventsResponse.ok) {
                    const resultData = await eventsResponse.json();
                    // Handle structure change: resultData might be array (old) or object (new)
                    const eventsList = Array.isArray(resultData) ? resultData : resultData.events;
                    const skillMap = resultData.skill_data || {};

                    // --- FRONTEND LOGIC FOR GOLD SKILLS (Stage 17) ---
                    // "Strict rule: Only check for color == 'Gold skills'"
                    const missingBaseIds = new Set();
                    Object.values(skillMap).forEach(skill => {
                        if (skill.color === 'Gold skills' && skill.base_skill_id) {
                            if (!skillMap[skill.base_skill_id]) {
                                missingBaseIds.add(skill.base_skill_id);
                            }
                        }
                    });

                    // Fetch missing base skills if any
                    if (missingBaseIds.size > 0) {
                        try {
                            const batchResponse = await fetch(`${API_BASE_URL}/api/skills/batch`, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json'
                                },
                                body: JSON.stringify({ skill_ids: Array.from(missingBaseIds) })
                            });

                            if (batchResponse.ok) {
                                const missingSkills = await batchResponse.json();
                                missingSkills.forEach(skill => {
                                    skillMap[skill.Id_skill] = {
                                        icon_id: skill.icon_id,
                                        name_en: skill.Skill_name,
                                        name_jp: skill.Skill_name_jp,
                                        color: skill.color,
                                        base_skill_id: skill.base_skill_id
                                    };
                                });
                            }
                        } catch (e) {
                            console.error("Failed to fetch missing base skills:", e);
                        }
                    }
                    // ------------------------------------------------

                    setEvents(eventsList);

                    // Extract Skills from Events
                    const extractedSkills = [];
                    const seenSkills = new Set();

                    eventsList.forEach(event => {
                        event.options.forEach(option => {
                            option.effects.forEach(effect => {
                                if (effect.skill_id && !seenSkills.has(effect.skill_id)) {
                                    seenSkills.add(effect.skill_id);

                                    // Lookup skill info
                                    const skillInfo = skillMap[effect.skill_id];
                                    const iconId = skillInfo ? skillInfo.icon_id : effect.skill_id; // Fallback to ID if not found
                                    const skillName = skillInfo ? (skillInfo.name_en || skillInfo.name_jp) : effect.text.replace(/ hint \+\d+/, '');

                                    extractedSkills.push({
                                        id: effect.skill_id,
                                        skill_name: skillName,
                                        icon_id: iconId
                                    });
                                }
                            });
                        });
                    });

                    // --- APPEND IMPLICIT BASE SKILLS ---
                    // Add base skills for any Gold skills found, even if not in events
                    Object.values(skillMap).forEach(skill => {
                        if (skill.color === 'Gold skills' && skill.base_skill_id && skillMap[skill.base_skill_id]) {
                            const baseId = skill.base_skill_id;
                            if (!seenSkills.has(baseId)) {
                                const baseSkill = skillMap[baseId];
                                extractedSkills.push({
                                    id: baseId,
                                    skill_name: baseSkill.name_en || baseSkill.name_jp,
                                    icon_id: baseSkill.icon_id
                                });
                                seenSkills.add(baseId);
                            }
                        }
                    });
                    // -----------------------------------

                    setEventSkills(extractedSkills);
                }
            } catch (err) {
                console.error(err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchSupportDetails();
    }, [id]);

    if (loading) return <LoadingScreen />;
    if (error) return <div className="min-h-screen pt-24 text-center text-red-600">Error: {error}</div>;
    if (!data) return null;

    const { info, skills, unique_effects, normal_effects } = data;

    // Helper for paths
    const getArtPath = () => {
        const originalPath = `/images/Support/Art/Support_Card_${info.support_id}.png`;
        return `${API_BASE_URL}/api/thumb?img=${originalPath}&width=410`;
    };
    const getCardPath = () => {
        const originalPath = `/images/Support/Card/Support_Card_${info.support_id}_Card.png`;
        return `${API_BASE_URL}/api/thumb?img=${originalPath}&width=160`;
    };
    const getRarityIconPath = () => `/images/Icon/Rarity_Icon/Support_Rarity_${info.rarity}_Pill.png`;

    const getTypeIconPath = (typeStr) => {
        const type = typeStr.charAt(0).toUpperCase() + typeStr.slice(1).toLowerCase();
        if (['Speed', 'Stamina', 'Power', 'Guts', 'Wit'].includes(type)) {
            return `/images/Icon/Stats_icons/Stat_Type_${type}.png`;
        } else if (['Pal', 'Group'].includes(type)) {
            return `/images/Icon/Support_Icons/Support_Type_${type}.png`;
        }
        return '';
    };

    const getSkillIconPath = (iconId) => `/images/Icon/Skill_Icons/icon_skill_${iconId}.png`;

    return (
        <div className="min-h-screen bg-panel-gray pt-20 pb-24">
            <div className="max-w-7xl mx-auto p-4 md:p-6 lg:p-8 space-y-8">

                {/* Top Actions Row */}
                <div className="flex items-center justify-between">
                    <button
                        onClick={() => navigate(ROUTES.CHARACTERS_SUPPORT)}
                        className="group flex items-center px-5 py-2.5 bg-white text-gray-700 rounded-full shadow-sm hover:shadow-md border border-gray-200 transition-all hover:bg-gray-50 hover:text-blue-600 font-medium"
                    >
                        <svg className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        {t('SupportDetail.back_to_gallery')}
                    </button>

                    {/* Admin Edit Button */}
                    {isAdmin() && (
                        <button
                            onClick={() => navigate(`${ROUTES.CHARACTERS_SUPPORT}/${id}/edit`)}
                            className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full shadow-md transition-colors font-semibold"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            {t('SupportDetail.edit_card')}
                        </button>
                    )}
                </div>

                {/* Top Section: Images & Info */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

                    {/* Visuals Column (Art Only - Scaled 75%) */}
                    <div className="flex justify-center items-start">
                        <div className="bg-white p-4 rounded-2xl shadow-xl border border-gray-100 w-3/4">
                            <div className="text-center mb-3 pb-2 border-b border-gray-100">
                                <h3 className="font-bold text-gray-400 text-xs tracking-[0.2em] uppercase">{t('SupportDetail.art_title')}</h3>
                            </div>
                            <div className="rounded-lg overflow-hidden">
                                <FadeImage
                                    src={getArtPath()}
                                    alt="Support Art"
                                    className="w-full h-auto object-cover"
                                    loading="lazy"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Info Column (Includes Card Image) */}
                    <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">

                        <div className="flex flex-col md:flex-row gap-6 items-start">
                            {/* Card Image moved here */}
                            <div className="w-32 md:w-40 flex-shrink-0 rounded-xl overflow-hidden shadow-md group">
                                <FadeImage
                                    src={getCardPath()}
                                    alt="Card"
                                    className="w-full h-auto transition-transform duration-300 group-hover:scale-105"
                                />
                            </div>

                            <div className="flex-grow w-full">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">{info.char_name_en || info.char_name_ja}</h1>
                                        <p className="text-lg text-gray-500 mt-1">{info.name_en || info.name_jp}</p>
                                    </div>
                                    {/* Rarity & Type Icons */}
                                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                        <img src={getTypeIconPath(info.Support_Type)} alt={info.Support_Type} className="w-10 h-10 object-contain" />
                                        <img src={getRarityIconPath()} alt={info.rarity} className="h-8 object-contain" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600 border-t pt-4">
                            <div>
                                <span className="font-semibold block">{t('SupportDetail.jp_name')}</span>
                                {info.name_jp}
                            </div>
                            <div>
                                <span className="font-semibold block">{t('SupportDetail.character_jp')}</span>
                                {info.char_name_ja}
                            </div>
                            <div>
                                <span className="font-semibold block">{t('SupportDetail.release_global')}</span>
                                {info.release_en ? new Date(info.release_en + 'T12:00:00').toLocaleDateString() : t('SupportDetail.unknown_date')}
                            </div>
                            <div>
                                <span className="font-semibold block">{t('SupportDetail.release_jp')}</span>
                                {info.release_jp ? new Date(info.release_jp + 'T12:00:00').toLocaleDateString() : t('SupportDetail.unknown_date')}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Hints Section */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <span className="w-1 h-6 bg-blue-500 rounded-full"></span>
                        {t('SupportDetail.hints_title')}
                    </h2>
                    {skills.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {skills.map((skill, idx) => (
                                <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100 hover:bg-white hover:shadow-sm transition-all">
                                    <img
                                        src={getSkillIconPath(skill.icon_id)}
                                        alt=""
                                        className="w-10 h-10 object-contain"
                                        onError={(e) => { e.target.src = '/images/placeholder.png'; }}
                                    />
                                    <span className="font-medium text-gray-800">{skill.skill_name}</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-gray-500 italic">{t('SupportDetail.no_hints')}</p>
                    )}
                </div>

                {/* Skill Events Section */}
                {eventSkills.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm p-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <span className="w-1 h-6 bg-purple-500 rounded-full"></span>
                            Ref. Skill Events
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {eventSkills.map((skill, idx) => (
                                <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100 hover:bg-white hover:shadow-sm transition-all">
                                    <img
                                        src={getSkillIconPath(skill.icon_id)}
                                        alt=""
                                        className="w-10 h-10 object-contain"
                                        onError={(e) => { e.target.src = '/images/placeholder.png'; }}
                                    />
                                    <span className="font-medium text-gray-800">{skill.skill_name}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Unique Effects Section */}
                {/* Unique Effects Section - Premium Design */}
                <div className="rounded-xl overflow-hidden shadow-md border border-amber-200 relative">
                    {/* Golden Header Background */}
                    <div className="bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-300 p-4 flex items-center gap-3 text-white shadow-sm">
                        <div className="p-2 bg-white/20 rounded-full backdrop-blur-sm">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                            </svg>
                        </div>
                        <h2 className="text-xl font-bold tracking-wide text-white drop-shadow-sm">
                            {t('SupportDetail.unique_effect_title')}
                        </h2>
                    </div>

                    {/* Content Body */}
                    <div className="bg-gradient-to-b from-amber-50 to-white p-6">
                        {unique_effects.length > 0 ? (
                            <div className="grid gap-4">
                                {unique_effects.map((effect, idx) => (
                                    <div
                                        key={idx}
                                        className="relative group bg-white p-5 rounded-xl border border-amber-100 shadow-sm hover:shadow-md hover:border-amber-300 transition-all duration-300"
                                    >
                                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                            {/* Effect Description */}
                                            <div className="flex-grow">
                                                <h3 className="font-bold text-amber-900 text-lg mb-1 group-hover:text-amber-700 transition-colors">
                                                    {t('SupportDetail.unique_bonus_title')}
                                                </h3>
                                                <p className="text-gray-700 leading-relaxed font-medium">
                                                    {effect.Effect}
                                                </p>
                                            </div>

                                            {/* Bonus Badge */}
                                            <div className="flex-shrink-0 self-start md:self-center">
                                                <span className="inline-block px-4 py-2 bg-amber-100 text-amber-800 border border-amber-200 rounded-lg font-mono font-bold text-lg shadow-inner">
                                                    {effect.Bonus}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Decorative Corner */}
                                        <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-bl from-amber-100/50 to-transparent rounded-tr-xl pointer-events-none"></div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <p className="text-gray-400 italic flex flex-col items-center gap-2">
                                    <span className="text-2xl grayscale opacity-50">✨</span>
                                    {t('SupportDetail.no_unique_effect')}
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Normal Effects Section */}
                <div className="bg-white rounded-xl shadow-sm p-6 overflow-x-auto">
                    <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <span className="w-1 h-6 bg-green-500 rounded-full"></span>
                        {t('SupportDetail.normal_effects_title')}
                    </h2>
                    {normal_effects.length > 0 ? (
                        <table className="w-full text-sm text-left text-gray-500">
                            <thead className="text-xs text-gray-700 bg-gray-50 border-b">
                                {/* Header 1: Crystal Icons */}
                                <tr>
                                    <th className="px-6 py-4 border-b">{t('SupportDetail.effect_header')}</th>
                                    {[0, 1, 2, 3, 4].map((capIndex) => (
                                        <th key={capIndex} className="px-2 py-4 text-center min-w-[80px]">
                                            <div className="flex justify-center items-end h-16 gap-0.5">
                                                {/* Logic for Crystals:
                                                    Cap 0: 1 Crystal (Grayscale)
                                                    Cap 1: 1 Crystal
                                                    Cap 2: 2 Crystals
                                                    Cap 3: 3 Crystals
                                                    Cap 4: 4 Crystals
                                                */}
                                                {capIndex === 0 ? (
                                                    <img
                                                        src="/images/Icon/Resources/CrystalCap.png"
                                                        alt="Limit Break 0"
                                                        className="w-8 h-auto grayscale opacity-70"
                                                    />
                                                ) : capIndex === 1 ? (
                                                    <img
                                                        src="/images/Icon/Resources/CrystalCap.png"
                                                        alt="Limit Break 1"
                                                        className="w-8 h-auto"
                                                    />
                                                ) : (
                                                    <div className="flex items-center gap-0.5">
                                                        <img
                                                            src="/images/Icon/Resources/CrystalCap.png"
                                                            alt={`Limit Break ${capIndex}`}
                                                            className="w-8 h-auto"
                                                        />
                                                        <span className="text-gray-900 font-bold text-xs">x{capIndex}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                                {/* Header 2: Levels */}
                                <tr className="bg-gray-100 text-gray-600 font-mono text-xs">
                                    <th className="px-6 py-2 text-left font-semibold">{t('SupportDetail.level_header')}</th>
                                    {(() => {
                                        let levels = [];
                                        if (info.rarity === 'SSR') levels = [30, 35, 40, 45, 50];
                                        else if (info.rarity === 'SR') levels = [25, 30, 35, 40, 45];
                                        else levels = [20, 25, 30, 35, 40]; // R & Others

                                        // REFACTOR: Use strict 'Lv' / 'Nv' logic if needed, but 'Lv' is standard for "Level" in many games.
                                        // However, strict instruction "Lv -> Nv" in Spanish.
                                        // I will simply output "Lv X" or "Nv X" based on current language?
                                        // No, I'll just put the number and maybe 'Lv' prefix from JSON?
                                        // Adding 'level_short' to json now for safety.
                                        // I'll assume 'Lv' is hardcoded in the loop below and replace it.

                                        return levels.map((lvl, idx) => (
                                            <th key={idx} className="px-2 py-2 text-center border-l border-gray-200">
                                                Lv {lvl}
                                            </th>
                                        ));
                                    })()}
                                </tr>
                            </thead>
                            <tbody>
                                {normal_effects.map((row, idx) => (
                                    <tr key={idx} className="bg-white border-b hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap border-r border-gray-100">
                                            {row.Effect}
                                        </td>
                                        {row.Bonuses.map((val, i) => (
                                            <td key={i} className="px-2 py-4 text-center border-l border-gray-100">
                                                {val !== null ? (
                                                    <span className="font-mono text-blue-600 font-bold bg-blue-50 px-2 py-1 rounded text-sm block w-full">
                                                        {val}
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-300">-</span>
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <p className="text-gray-500 italic">{t('SupportDetail.no_normal_effects')}</p>
                    )}
                </div>

                {/* Training Events Section */}
                <SupportEvents events={events} />

            </div>
        </div>
    );
};

export default SupportDetail;
// Revisado: 2025-12-18 I18n Updated
